import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { Upload, CheckCircle2, AlertTriangle, Copy, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// MOD-11: validated CSV import → preview (errors / duplicates) → background job → audit → workspace reload.
export function ImportClientsDialog({ open, onClose }) {
  const { loadWorkspace } = useApp();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [mode, setMode] = useState("skip");
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);

  useEffect(() => { if (!open) { setPreview(null); setJob(null); setBusy(false); } }, [open]);
  useEffect(() => {
    if (!job || ["COMPLETED", "FAILED", "CANCELLED"].includes(job.status)) return;
    const t = setInterval(() => axios.get(`${API}/jobs/${job.id}`).then((r) => setJob(r.data)).catch(() => {}), 1500);
    return () => clearInterval(t);
  }, [job]);
  useEffect(() => { if (job?.status === "COMPLETED") { loadWorkspace(); toast.success(`Import complete: ${job.result.created} created, ${job.result.updated} updated, ${job.result.skipped} skipped.`); } }, [job?.status]); // eslint-disable-line

  const pick = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { const { data } = await axios.post(`${API}/clients/import/preview`, fd); setPreview(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not read CSV."); }
    finally { setBusy(false); }
  };
  const commit = async () => {
    setBusy(true);
    try { const { data } = await axios.post(`${API}/clients/import/${preview.importId}/commit`, { mode }); setJob(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start import."); }
    finally { setBusy(false); }
  };
  const s = preview?.summary;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="import-clients-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Import clients from CSV</DialogTitle>
          <DialogDescription>Columns recognised: Name (required), Type, Mobile, Email, Address, GST, Status, Notes. Rows are validated and checked against existing clients before anything is written.</DialogDescription></DialogHeader>
        {!preview && !job && (
          <div className="rounded-xl border-2 border-dashed border-line p-8 text-center">
            <Upload className="mx-auto h-8 w-8 text-ink-muted" />
            <p className="mt-2 text-sm text-ink-2">Choose a .csv file (max 2 MB, 1000 rows)</p>
            <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => pick(e.target.files?.[0])} data-testid="import-csv-input" />
            <Button className="mt-3" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="import-choose-file">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Choose file</Button>
          </div>
        )}
        {preview && !job && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 text-center text-sm" data-testid="import-summary">
              {[["Rows", s.total, ""], ["Ready to create", s.valid, "text-success"], ["Duplicates", s.duplicates, "text-warning"], ["Invalid", s.invalid, "text-danger"]].map(([l, v, c]) => <div key={l} className="rounded-lg border border-line p-2"><div className={cn("font-display text-2xl font-semibold", c)}>{v}</div><div className="text-xs text-ink-2">{l}</div></div>)}
            </div>
            <div className="max-h-72 overflow-auto rounded-lg border border-line">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-surface text-left text-[10px] uppercase text-ink-muted"><tr><th className="px-2 py-1.5">Row</th><th className="px-2 py-1.5">Name</th><th className="px-2 py-1.5">Email</th><th className="px-2 py-1.5">Mobile</th><th className="px-2 py-1.5">Type</th><th className="px-2 py-1.5">Result</th></tr></thead>
                <tbody>{preview.rows.map((r) => (
                  <tr key={r.row} className="border-t border-line" data-testid={`import-row-${r.row}`}>
                    <td className="px-2 py-1.5 text-ink-muted">{r.row}</td><td className="px-2 py-1.5 text-ink">{r.data.name || <em className="text-ink-muted">missing</em>}</td><td className="px-2 py-1.5 text-ink-2">{r.data.email}</td><td className="px-2 py-1.5 text-ink-2">{r.data.mobile}</td><td className="px-2 py-1.5 text-ink-2">{r.data.type}</td>
                    <td className="px-2 py-1.5">{!r.valid ? <span className="flex items-center gap-1 text-danger"><AlertTriangle className="h-3 w-3" />{r.errors.join("; ")}</span> : r.duplicateOf ? <span className="flex items-center gap-1 text-warning"><Copy className="h-3 w-3" />Matches “{r.duplicateOf.name}”</span> : <span className="flex items-center gap-1 text-success"><CheckCircle2 className="h-3 w-3" />Will create</span>}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {s.duplicates > 0 && (
              <div className="flex items-center gap-3 text-sm">
                <span className="text-ink-2">Duplicates:</span>
                {[["skip", "Skip (keep existing)"], ["update", "Update existing with CSV values"]].map(([v, l]) => <label key={v} className="flex items-center gap-1.5"><input type="radio" name="dupmode" checked={mode === v} onChange={() => setMode(v)} data-testid={`import-mode-${v}`} />{l}</label>)}
              </div>
            )}
          </div>
        )}
        {job && (
          <div className="rounded-lg border border-line p-4 text-sm" data-testid="import-job-status">
            <div className="flex items-center gap-2 font-medium text-ink">{job.status === "COMPLETED" ? <CheckCircle2 className="h-4 w-4 text-success" /> : job.status === "FAILED" ? <AlertTriangle className="h-4 w-4 text-danger" /> : <Loader2 className="h-4 w-4 animate-spin text-copper" />}Import {job.status.toLowerCase()} · {job.progress?.stage}</div>
            {job.result && <div className="mt-2 text-ink-2">{job.result.created} created · {job.result.updated} updated · {job.result.skipped} skipped. Every row is recorded in the audit log.</div>}
            {job.error && <div className="mt-2 text-danger">{job.error}</div>}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{job ? "Close" : "Cancel"}</Button>
          {preview && !job && <Button disabled={busy || (s.valid === 0 && !(mode === "update" && s.duplicates > 0))} onClick={commit} data-testid="import-commit-btn">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Import {s.valid}{mode === "update" && s.duplicates > 0 ? ` + update ${s.duplicates}` : ""}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
